import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Loader2, ArrowRight } from "lucide-react";

export default function NDAGate({ children }) {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [ndaText, setNdaText] = useState(null);
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!user || user.role !== "founder") { setStatus({ accepted: true }); return; }
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get("/auth/nda/status"),
          api.get("/auth/nda/text"),
        ]);
        setStatus(s.data);
        setNdaText(t.data);
        setName(user.name || "");
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.warn("[NDAGate] init failed", e);
      }
    })();
  }, [user]);

  if (!user || user.role !== "founder") return children;
  if (status === null) return <div className="flex min-h-screen items-center justify-center bg-[#F4F8F8]"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]"/></div>;
  if (status.accepted) return children;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/auth/nda/accept", { accepted: checked, full_name: name });
      await refresh();
      // Stay on the founder's original destination (e.g. /welcome after
      // signup) — flipping local status re-renders the gated children.
      setStatus({ accepted: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't record acceptance");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-[#0A8B85]"/>
          <h1 className="text-3xl font-bold text-neutral-900">Before you start: NDA + Non-circumvent</h1>
        </div>
        <p className="mt-2 text-sm text-neutral-600">
          Clinq protects your business by default. Your information is shared <b>only</b> with funders you pre-approve, and funders can&apos;t bypass us.
          Please read the agreement and accept to continue.
        </p>

        <div className="mt-6 max-h-[60vh] overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-6 text-sm leading-6 text-neutral-700 whitespace-pre-line" data-testid="nda-text">
          {ndaText?.text}
        </div>

        <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6">
          <label className="flex items-start gap-3">
            <input data-testid="nda-checkbox" type="checkbox" checked={checked}
              onChange={e => setChecked(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-neutral-300 text-[#0A8B85] focus:ring-[#0A8B85]"/>
            <span className="text-sm text-neutral-700">
              I have read and agree to the Clinq Mutual NDA &amp; Non-circumvent Agreement (Version {ndaText?.version}). I understand my information will only be shared with funders I pre-approve, and I will not bypass Clinq to contact any introduced funder for 24 months.
            </span>
          </label>
          <div className="mt-4">
            <label className="text-xs font-semibold text-neutral-600">Type your full name to e-sign</label>
            <input data-testid="nda-name" value={name} onChange={e => setName(e.target.value)}
              placeholder="Your full legal name"
              className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]"/>
          </div>
          {err && <p className="mt-3 text-sm text-red-600" data-testid="nda-err">{err}</p>}
          <button onClick={submit} disabled={busy || !checked || !name.trim()} data-testid="nda-submit"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
            {busy ? "Recording…" : "Accept & continue"}
            {!busy && <ArrowRight className="h-4 w-4"/>}
          </button>
        </div>
      </div>
    </div>
  );
}
